import type { LapRecord } from '../game/replay';
import { loadDay } from '../game/storage';
import type { Board, LinkNoLap, Paper, Podium, ShareBody, SubmitBody, SubmitResponse, WatchLap, Winner } from './protocol';

/**
 * API client. HttpApi talks to the game's own server (worker/): the daily
 * leaderboard now; friends' ghosts, server-side lap verification, coins and
 * the rest later. Any network failure degrades to "offline": the game never
 * waits on the server to be playable.
 */
export interface GranPremioApi {
  /** The day's board for this circuit and car, or null when the server can't be reached. */
  /** `guild`: a Discord server's own board (its players' laps only). */
  getLeaderboard(date: string, trackHash: string, physicsHash: string, playerId: string, guild?: string | null): Promise<Board | null>;
  submitLap(body: SubmitBody): Promise<SubmitResponse>;
  /** Best sector times of the day across all players ("purple" sectors). */
  getDayBestSectors(date: string, trackHash: string, physicsHash: string): Promise<(number | null)[]>;
  getGhosts(date: string): Promise<LapRecord[]>;
  /** Store a lap behind a link. */
  createShare(body: ShareBody): Promise<{ ok: boolean; reason?: string }>;
  /** A shared lap; 'missing' if the link leads nowhere, null if the server can't be reached. */
  /** A friend's link: its driver's lap today, or (none today) who sent it and when; 'missing' for no such link. */
  getShare(id: string): Promise<WatchLap | LinkNoLap | 'missing' | null>;
  /** One lap from a board, by its public handle, if this player may watch it (null if not, if it's gone, or offline). */
  getBoardLap(date: string, trackHash: string, physicsHash: string, ref: string, playerId: string, guild?: string | null): Promise<WatchLap | null>;
  /** A day's winner, or null. */
  getWinner(date: string): Promise<Winner | null>;
  /** A day's podium (its top three, every lap watchable once the day is over), or null. */
  getPodium(date: string): Promise<Podium | null>;
  /** The morning paper: yesterday's race for this player, or null (nothing to tell, or offline). */
  getPaper(playerId: string): Promise<Paper | null>;
}

export class HttpApi implements GranPremioApi {
  /** Relative to the page, so the game also works when served from a sub-path. */
  constructor(private base = 'api/') {}

  async getLeaderboard(date: string, trackHash: string, physicsHash: string, playerId: string, guild: string | null = null): Promise<Board | null> {
    const q = new URLSearchParams({ date, track: trackHash, physics: physicsHash, player: playerId, ...(guild ? { guild } : {}) });
    try {
      const r = await fetch(`${this.base}leaderboard?${q}`, { cache: 'no-store' });
      return r.ok ? ((await r.json()) as Board) : null;
    } catch {
      return null;
    }
  }

  async submitLap(body: SubmitBody): Promise<SubmitResponse> {
    try {
      const r = await fetch(`${this.base}laps`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(body),
      });
      const out = (await r.json().catch(() => null)) as SubmitResponse | null;
      return out ?? { accepted: false, reason: `server error ${r.status}` };
    } catch {
      return { accepted: false, reason: 'offline' };
    }
  }

  async getDayBestSectors(date: string, trackHash: string, physicsHash: string): Promise<(number | null)[]> {
    // For now "best of the day" is your own best.
    return loadDay(date, trackHash, physicsHash).bestSectors.slice();
  }

  async getGhosts(): Promise<LapRecord[]> {
    return [];
  }

  async createShare(body: ShareBody): Promise<{ ok: boolean; reason?: string }> {
    try {
      const r = await fetch(`${this.base}shares`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(body),
      });
      return ((await r.json().catch(() => null)) as { ok: boolean; reason?: string } | null) ?? { ok: false, reason: `server error ${r.status}` };
    } catch {
      return { ok: false, reason: 'offline' };
    }
  }

  async getShare(id: string): Promise<WatchLap | LinkNoLap | 'missing' | null> {
    try {
      const r = await fetch(`${this.base}shares/${encodeURIComponent(id)}`);
      if (r.status === 404) return 'missing';
      return r.ok ? ((await r.json()) as WatchLap | LinkNoLap) : null;
    } catch {
      return null;
    }
  }

  async getBoardLap(date: string, trackHash: string, physicsHash: string, ref: string, playerId: string, guild: string | null = null): Promise<WatchLap | null> {
    const q = new URLSearchParams({ date, track: trackHash, physics: physicsHash, ref, player: playerId, ...(guild ? { guild } : {}) });
    try {
      const r = await fetch(`${this.base}lap?${q}`);
      return r.ok ? ((await r.json()) as WatchLap) : null;
    } catch {
      return null;
    }
  }

  async getWinner(date: string): Promise<Winner | null> {
    try {
      const r = await fetch(`${this.base}winner?${new URLSearchParams({ date })}`);
      return r.ok ? (((await r.json()) as { winner: Winner | null }).winner ?? null) : null;
    } catch {
      return null;
    }
  }

  async getPaper(playerId: string): Promise<Paper | null> {
    try {
      const r = await fetch(`${this.base}paper?${new URLSearchParams({ player: playerId })}`, { cache: 'no-store' });
      return r.ok ? (((await r.json()) as { paper: Paper | null }).paper ?? null) : null;
    } catch {
      return null;
    }
  }

  async getPodium(date: string): Promise<Podium | null> {
    try {
      const r = await fetch(`${this.base}winner?${new URLSearchParams({ date })}`);
      if (!r.ok) return null;
      const o = (await r.json()) as { winner: Winner | null; podium?: Podium | null };
      if (o.podium) return o.podium;
      // A server from before the podium: its winner, alone on the top step.
      const w = o.winner;
      return w ? { date: w.date, era: w.era, trackHash: w.trackHash, physicsHash: w.physicsHash, total: w.total, places: [{ place: 1, name: w.name, lapMs: w.lapMs, ref: w.ref }] } : null;
    } catch {
      return null;
    }
  }
}
